import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Modal } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import TextareaEmailsField from 'components/TextareaEmailsField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomRFI } from 'router/subrouters/DataRoom/models/DataRoomRfi';
import { getArrayOfEmails, getIsArrayEmpty, isEmail } from 'utils';

import './RfiAddRecipientsModal.styles.scss';

type RfiAddRecipientsModalProps = {
  className?: string;
  dataRoomRfiId: string;
  existingRecipients: string[];
  onRfiUpdate: (rfi: DataRoomRFI) => void;
};

const RfiAddRecipientsModal: ForwardRefRenderFunction<
  ModalRef,
  RfiAddRecipientsModalProps
> = ({ className, dataRoomRfiId, existingRecipients, onRfiUpdate }, ref) => {
  const classes = classNames('rfi-add-recipients-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { t } = useTranslation();
  const { popServerError } = useContext(PopNotificationsContext);

  const onSubmit = useCallback(
    async ({
      recipients,
      recipientsUnsubmited,
    }: {
      recipients: string[];
      recipientsUnsubmited: string;
    }) => {
      const finalEmails = getArrayOfEmails(recipients, recipientsUnsubmited);

      try {
        const { data } = await dataRoomApi.rfi.patchRfi(dataRoomRfiId, {
          recipients: [...new Set([...existingRecipients, ...finalEmails])],
        });

        onRfiUpdate(data);
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [dataRoomRfiId, existingRecipients, modal, onRfiUpdate, popServerError],
  );

  return (
    <Modal ref={modalRef} className={classes} title={t('addRecipients')}>
      <Form
        initialValues={{ recipients: [] }}
        onSubmit={onSubmit}
        render={({
          submitting,
          values: { recipients, recipientsUnsubmited },
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="recipients"
              component={TextareaEmailsField}
              description={t('rfiEmailsDescription')}
            />

            <div className="rfi-add-recipients-modal__actions">
              <Button
                type="submit"
                disabled={
                  submitting ||
                  (getIsArrayEmpty(recipients) &&
                    !isEmail(recipientsUnsubmited))
                }
              >
                {t('done')}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(RfiAddRecipientsModal);
